/**
 * FileMenu — product lifecycle + ingest + compiled deliverables.
 *
 * Import ▸  routes to the real ingest surfaces (document extraction lives in the
 *           Requirements → Import tab; connector sync in Integrations).
 * Generate ▸ surfaces the auto-compiled eQMS artifacts that are the wedge —
 *           each deep-links to the section/tab that renders + signs it.
 */
import React, { useState } from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add,
  ChevronRight,
  Description,
  FactCheck,
  FilePresent,
  FolderOpen,
  GavelOutlined,
  GridOn,
  Hub,
  Info,
  Logout,
  PlaylistAddCheck,
  Science,
  Upload,
} from '@mui/icons-material';
import { COMPLIANCE_TABS, CONFORMANCE_MODE, REPORTS_SUBTAB } from '../../../constants/complianceTabs';

const FileMenu = ({
  anchorEl,
  open,
  onClose,
  hasProject,
  onSectionChange,
  onNewProject,
  onOpenProject,
  onProperties,
  onSignOut,
}) => {
  const [importAnchor, setImportAnchor] = useState(null);
  const [generateAnchor, setGenerateAnchor] = useState(null);

  const closeAll = () => {
    setImportAnchor(null);
    setGenerateAnchor(null);
    onClose();
  };
  const wrap = (fn) => () => { closeAll(); fn?.(); };
  const nav = (section, tab = 0, subTab = 0) => wrap(() => onSectionChange?.(section, tab, subTab));

  const row = (icon, label) => (
    <>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>{label}</ListItemText>
    </>
  );

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <MenuItem dense onClick={wrap(onNewProject)}>
          {row(<Add fontSize="small" />, 'New Product…')}
        </MenuItem>
        <MenuItem dense onClick={wrap(onOpenProject)}>
          {row(<FolderOpen fontSize="small" />, 'Open Product…')}
        </MenuItem>
        <Divider />

        <Tooltip title={hasProject ? '' : 'Select a product first'} placement="right">
          <span>
            <MenuItem
              dense
              disabled={!hasProject}
              onClick={(e) => setImportAnchor(e.currentTarget)}
            >
              {row(<Upload fontSize="small" />, 'Import')}
              <ChevronRight fontSize="small" />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={hasProject ? '' : 'Select a product first'} placement="right">
          <span>
            <MenuItem
              dense
              disabled={!hasProject}
              onClick={(e) => setGenerateAnchor(e.currentTarget)}
            >
              {row(<FactCheck fontSize="small" />, 'Generate')}
              <ChevronRight fontSize="small" />
            </MenuItem>
          </span>
        </Tooltip>
        <Divider />

        <Tooltip title={hasProject ? '' : 'Select a product first'} placement="right">
          <span>
            <MenuItem dense disabled={!hasProject} onClick={wrap(onProperties)}>
              {row(<Info fontSize="small" />, 'Product Properties…')}
            </MenuItem>
          </span>
        </Tooltip>
        <Divider />
        <MenuItem dense onClick={wrap(onSignOut)}>
          {row(<Logout fontSize="small" />, 'Sign Out')}
        </MenuItem>
      </Menu>

      {/* Import ▸ */}
      <Menu
        anchorEl={importAnchor}
        open={Boolean(importAnchor)}
        onClose={() => setImportAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem dense onClick={nav('requirements', 1)}>
          {row(<Description fontSize="small" />, 'Requirements (from document)…')}
        </MenuItem>
        <MenuItem dense onClick={nav('integrations')}>
          {row(<Hub fontSize="small" />, 'From Connector…')}
        </MenuItem>
      </Menu>

      {/* Generate ▸ */}
      <Menu
        anchorEl={generateAnchor}
        open={Boolean(generateAnchor)}
        onClose={() => setGenerateAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem dense onClick={nav('declaration-of-conformity')}>
          {row(<GavelOutlined fontSize="small" />, 'Declaration of Conformity…')}
        </MenuItem>
        <MenuItem dense onClick={nav('compliance', COMPLIANCE_TABS.reports, REPORTS_SUBTAB.technicalFile)}>
          {row(<FilePresent fontSize="small" />, 'Technical File (EU MDR)…')}
        </MenuItem>
        <MenuItem dense onClick={nav('vv', 0)}>
          {row(<Science fontSize="small" />, 'V&V / FAT Report…')}
        </MenuItem>
        <MenuItem dense onClick={nav('vv', 2)}>
          {row(<PlaylistAddCheck fontSize="small" />, 'IQ / OQ / PQ Protocol…')}
        </MenuItem>
        <MenuItem dense onClick={nav('compliance', COMPLIANCE_TABS.conformance, CONFORMANCE_MODE.coverage)}>
          {row(<GridOn fontSize="small" />, 'Traceability…')}
        </MenuItem>
        <MenuItem dense onClick={nav('compliance', COMPLIANCE_TABS.reports, REPORTS_SUBTAB.auditPackage)}>
          {row(<FactCheck fontSize="small" />, 'Audit Package…')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default FileMenu;
